import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, NgZone } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom, timeout } from 'rxjs';
import { SectionHeadingComponent } from '../../../../components/section-heading/section-heading.component';
import { BookingApiService } from '../../../../core/services/booking-api.service';
import { RecaptchaService } from '../../../../core/services/recaptcha.service';
import {
  AvailabilityStatus,
  AvailabilitySlotDto,
  BookingRequestDto,
  OnboardingBusinessContract
} from '../../models/booking.models';
import {
  copyBillingToShipping,
  createOnboardingBookingForm,
  OnboardingBookingFormGroup
} from '../../forms/booking.form';

interface CalendarDay {
  isoDate: string;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  status: AvailabilityStatus;
  isSelected: boolean;
  isDisabled: boolean;
}

@Component({
  selector: 'app-onboarding-booking-section',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SectionHeadingComponent],
  templateUrl: './onboarding-booking-section.component.html',
  styleUrl: './onboarding-booking-section.component.css'
})
export class OnboardingBookingSectionComponent {
  private readonly fb = inject(FormBuilder);
  private readonly bookingApi = inject(BookingApiService);
  private readonly recaptchaService = inject(RecaptchaService);
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly form: OnboardingBookingFormGroup = createOnboardingBookingForm(this.fb.nonNullable);
  protected submitting = false;
  protected loadingAvailability = false;
  protected loadingCalendar = false;
  protected successMessage = '';
  protected errorMessage = '';
  protected availabilityMessage = '';
  protected calendarMessage = '';
  protected dayStatusByDate: Record<string, AvailabilityStatus> = {};
  protected currentMonth = this.toMonthStart(new Date());
  protected calendarWeeks: CalendarDay[][] = [];
  protected readonly weekDayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  protected readonly browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  private readonly browserLocale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';

  protected slotOptions: AvailabilitySlotDto[] = [];
  protected readonly minSelectableDate = this.toIsoDate(new Date());
  protected readonly maxSelectableDate = this.toIsoDate(this.addDays(new Date(), 89));
  private loadedMonthKey = '';

  constructor() {
    if (this.form.controls.shipping.controls.sameAsBilling.value) {
      copyBillingToShipping(this.form);
    }

    this.form.controls.shipping.controls.sameAsBilling.valueChanges.subscribe((sameAsBilling) => {
      if (sameAsBilling) {
        copyBillingToShipping(this.form);
      }
    });

    this.form.controls.billing.valueChanges.subscribe(() => {
      if (this.form.controls.shipping.controls.sameAsBilling.value) {
        copyBillingToShipping(this.form);
      }
    });

    this.form.controls.booking.controls.selectedDate.valueChanges.subscribe(async (selectedDate) => {
      if (selectedDate && !this.isSameCalendarMonth(selectedDate, this.currentMonth)) {
        this.currentMonth = this.toMonthStart(this.parseIsoToDate(selectedDate));
        this.loadedMonthKey = '';
        await this.refreshCurrentMonthAvailability();
      }

      await this.loadAvailability(selectedDate);
      this.generateCalendar();
    });

    const initialDate = this.minSelectableDate;
    this.form.controls.booking.controls.selectedDate.setValue(initialDate, { emitEvent: false });
    this.currentMonth = this.toMonthStart(this.parseIsoToDate(initialDate));
    this.generateCalendar();

    void this.initializeAvailability(initialDate);
  }

  private async initializeAvailability(initialDate: string): Promise<void> {
    await this.refreshCurrentMonthAvailability();
    await this.loadAvailability(initialDate);
  }

  protected get currentMonthLabel(): string {
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(this.currentMonth);
  }

  protected get selectedDateDisplay(): string {
    const value = this.form.controls.booking.controls.selectedDate.value;
    if (!value) {
      return '';
    }

    return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(this.parseIsoToDate(value));
  }

  protected get selectedSlotDisplay(): string {
    const selectedSlot = this.slotOptions.find((slot) => slot.startUtc === this.form.controls.booking.controls.selectedSlot.value);
    return selectedSlot ? this.formatSlotOptionLabel(selectedSlot) : '';
  }

  protected formatSlotOptionLabel(slot: AvailabilitySlotDto): string {
    const slotStart = new Date(slot.startUtc);
    if (Number.isNaN(slotStart.getTime())) {
      return `${slot.displayTime} (${slot.timeZone})`;
    }

    return new Intl.DateTimeFormat(this.browserLocale, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: this.browserTimeZone,
      timeZoneName: 'short'
    }).format(slotStart);
  }

  protected canNavigateMonth(direction: number): boolean {
    const target = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + direction, 1);
    const targetStart = this.toIsoDate(target);
    const targetEnd = this.toIsoDate(new Date(target.getFullYear(), target.getMonth() + 1, 0));

    return !(targetEnd < this.minSelectableDate || targetStart > this.maxSelectableDate);
  }

  protected async navigateMonth(direction: number): Promise<void> {
    if (!this.canNavigateMonth(direction)) {
      return;
    }

    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + direction, 1);
    this.loadedMonthKey = '';
    this.generateCalendar();
    await this.refreshCurrentMonthAvailability();
  }

  protected async selectCalendarDay(day: CalendarDay): Promise<void> {
    if (day.isDisabled) {
      return;
    }

    this.form.controls.booking.controls.selectedDate.setValue(day.isoDate);
  }

  protected async onCalendarDayKeydown(event: KeyboardEvent, day: CalendarDay): Promise<void> {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        await this.selectCalendarDay(day);
        return;
      case 'ArrowLeft':
        event.preventDefault();
        await this.moveCalendarFocus(day.isoDate, -1);
        return;
      case 'ArrowRight':
        event.preventDefault();
        await this.moveCalendarFocus(day.isoDate, 1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        await this.moveCalendarFocus(day.isoDate, -7);
        return;
      case 'ArrowDown':
        event.preventDefault();
        await this.moveCalendarFocus(day.isoDate, 7);
        return;
      default:
        return;
    }
  }

  protected getCalendarDayTabIndex(day: CalendarDay): number {
    if (day.isDisabled) {
      return -1;
    }

    const selected = this.form.controls.booking.controls.selectedDate.value;
    if (selected) {
      return day.isoDate === selected ? 0 : -1;
    }

    const firstSelectable = this.calendarWeeks.flat().find((calendarDay) => !calendarDay.isDisabled);
    return firstSelectable?.isoDate === day.isoDate ? 0 : -1;
  }

  protected onCopyShippingFromBilling(): void {
    if (this.form.controls.shipping.controls.sameAsBilling.value) {
      copyBillingToShipping(this.form);
    }
  }

  protected async submit(): Promise<void> {
    this.successMessage = '';
    this.errorMessage = '';

    if (this.form.controls.honeypot.value) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage = 'Please complete all required fields before submitting.';
      return;
    }

    try {
      this.setSubmittingState(true);

      if (this.form.controls.shipping.controls.sameAsBilling.value) {
        copyBillingToShipping(this.form);
      }

      if (!this.slotOptions.some((slot) => slot.startUtc === this.form.controls.booking.controls.selectedSlot.value)) {
        this.errorMessage = 'Please select an available time slot.';
        return;
      }

      const recaptchaToken = await this.recaptchaService.executeBookingRequest();
      const request = this.buildRequest(recaptchaToken);
      const response = await firstValueFrom(this.bookingApi.submitBooking(request));

      this.ngZone.run(() => {
        this.successMessage = response.message || 'Booking request submitted successfully.';
        this.cdr.detectChanges();
      });
    } catch (error: unknown) {
      this.ngZone.run(() => {
        this.errorMessage = this.getSubmitErrorMessage(error);
        this.cdr.detectChanges();
      });
    } finally {
      this.setSubmittingState(false);
    }
  }

  private setSubmittingState(isSubmitting: boolean): void {
    this.ngZone.run(() => {
      this.submitting = isSubmitting;
      this.cdr.detectChanges();
    });
  }

  private getSubmitErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string' && error.error.trim().length > 0) {
        return error.error;
      }

      if (
        error.error &&
        typeof error.error === 'object' &&
        'message' in error.error &&
        typeof error.error.message === 'string' &&
        error.error.message.trim().length > 0
      ) {
        return error.error.message;
      }
    }

    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message;
    }

    return 'Booking request failed.';
  }

  private buildRequest(recaptchaToken: string): BookingRequestDto {
    const booking = this.form.controls.booking.controls;
    const company = this.form.controls.company.controls;
    const billing = this.form.controls.billing.controls;
    const shipping = this.form.controls.shipping.controls;
    const selectedSlot = this.slotOptions.find((slot) => slot.startUtc === booking.selectedSlot.value);

    if (!selectedSlot) {
      throw new Error('Selected slot is invalid.');
    }

    const payload: OnboardingBusinessContract = {
      ContactName: company.ContactName.value.trim(),
      DisplayName: company.DisplayName.value.trim(),
      LegalName: company.LegalName.value.trim(),
      ContactEmail: company.ContactEmail.value.trim(),

      BillingBusinessName: billing.BillingBusinessName.value.trim(),
      BillingContactName: billing.BillingContactName.value.trim(),
      BillingContactEmail: billing.BillingContactEmail.value.trim(),
      BillingContactPhone: billing.BillingContactPhone.value.trim(),
      BillingAddress1: billing.BillingAddress1.value.trim(),
      BillingAddress2: billing.BillingAddress2.value.trim(),
      BillingCity: billing.BillingCity.value.trim(),
      BillingState: billing.BillingState.value.trim(),
      BillingZipCode: billing.BillingZipCode.value.trim(),
      BillingCountry: billing.BillingCountry.value.trim(),

      ShippingBusinessName: shipping.ShippingBusinessName.value.trim(),
      ShippingContactName: shipping.ShippingContactName.value.trim(),
      ShippingContactEmail: shipping.ShippingContactEmail.value.trim(),
      ShippingContactPhone: shipping.ShippingContactPhone.value.trim(),
      ShippingAddress1: shipping.ShippingAddress1.value.trim(),
      ShippingAddress2: shipping.ShippingAddress2.value.trim(),
      ShippingCity: shipping.ShippingCity.value.trim(),
      ShippingState: shipping.ShippingState.value.trim(),
      ShippingZipCode: shipping.ShippingZipCode.value.trim(),
      ShippingCountry: shipping.ShippingCountry.value.trim()
    };

    return {
      requestedStartUtc: selectedSlot.startUtc,
      requestedEndUtc: selectedSlot.endUtc,
      organizerTimeZone: selectedSlot.timeZone,
      recaptchaToken,
      sameAsBilling: shipping.sameAsBilling.value,
      payload
    };
  }

  private async loadAvailability(selectedDate: string): Promise<void> {
    const selectedSlotControl = this.form.controls.booking.controls.selectedSlot;
    this.ngZone.run(() => {
      selectedSlotControl.setValue('');
      selectedSlotControl.disable({ emitEvent: false });
      this.availabilityMessage = '';
      this.slotOptions = [];
    });

    if (!selectedDate) {
      return;
    }

    try {
      this.ngZone.run(() => {
        this.loadingAvailability = true;
      });

      const recaptchaToken = await this.executeBookingRequestRecaptcha();
      const response = await firstValueFrom(this.bookingApi.getAvailability(selectedDate, selectedDate, recaptchaToken).pipe(timeout(10000)));
      const slots = response.slots;
      this.ngZone.run(() => {
        this.slotOptions = slots;

        if (slots.length) {
          selectedSlotControl.enable({ emitEvent: false });
        } else {
          this.availabilityMessage = 'No slots are available for the selected date.';
        }
      });
    } catch {
      this.ngZone.run(() => {
        this.availabilityMessage = 'Unable to load availability. Please try another date.';
      });
    } finally {
      this.ngZone.run(() => {
        this.loadingAvailability = false;
        this.cdr.detectChanges();
      });
    }
  }

  private async refreshCurrentMonthAvailability(): Promise<void> {
    this.ngZone.run(() => {
      this.calendarMessage = '';
    });

    const monthKey = `${this.currentMonth.getFullYear()}-${String(this.currentMonth.getMonth() + 1).padStart(2, '0')}`;
    if (monthKey === this.loadedMonthKey) {
      return;
    }

    const startDate = `${this.currentMonth.getFullYear()}-${String(this.currentMonth.getMonth() + 1).padStart(2, '0')}-01`;
    const endDate = this.toIsoDate(new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0));

    const requestStart = startDate < this.minSelectableDate ? this.minSelectableDate : startDate;
    const requestEnd = endDate > this.maxSelectableDate ? this.maxSelectableDate : endDate;

    if (requestStart > requestEnd) {
      this.ngZone.run(() => {
        this.dayStatusByDate = {};
        this.calendarMessage = 'This month is outside the current booking window.';
      });
      this.loadedMonthKey = monthKey;
      this.generateCalendar();
      return;
    }

    try {
      this.ngZone.run(() => {
        this.loadingCalendar = true;
      });

      const recaptchaToken = await this.executeBookingRequestRecaptcha();
      const response = await firstValueFrom(this.bookingApi.getAvailability(requestStart, requestEnd, recaptchaToken).pipe(timeout(10000)));

      const dayStatusByDate = response.days.reduce<Record<string, AvailabilityStatus>>((acc, day) => {
        acc[day.date] = day.status;
        return acc;
      }, {});

      const hasAvailableDate = response.days.some((day) => day.status === 'available');
      this.ngZone.run(() => {
        this.dayStatusByDate = dayStatusByDate;

        if (!hasAvailableDate) {
          this.calendarMessage = 'No available dates were returned for this month.';
        }
      });

      this.loadedMonthKey = monthKey;
    } catch {
      this.ngZone.run(() => {
        this.calendarMessage = 'Unable to load calendar availability for this month.';
        this.dayStatusByDate = {};
      });
    } finally {
      this.ngZone.run(() => {
        this.loadingCalendar = false;
        this.generateCalendar();
        this.cdr.detectChanges();
      });
    }
  }

  private generateCalendar(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const selectedDate = this.form.controls.booking.controls.selectedDate.value;
    const firstDayOfMonth = new Date(year, month, 1);
    const startOffset = firstDayOfMonth.getDay();
    const cursor = new Date(year, month, 1 - startOffset);

    const weeks: CalendarDay[][] = [];
    for (let weekIndex = 0; weekIndex < 6; weekIndex += 1) {
      const week: CalendarDay[] = [];

      for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
        const isoDate = this.toIsoDate(cursor);
        const isCurrentMonth = cursor.getMonth() === month;
        const inRange = this.isWithinSelectableRange(isoDate);
        const status = isCurrentMonth && inRange ? (this.dayStatusByDate[isoDate] ?? 'full') : 'full';

        week.push({
          isoDate,
          dayOfMonth: cursor.getDate(),
          isCurrentMonth,
          status,
          isSelected: selectedDate === isoDate,
          isDisabled: !isCurrentMonth || !inRange || status === 'full'
        });

        cursor.setDate(cursor.getDate() + 1);
      }

      weeks.push(week);
    }

    this.calendarWeeks = weeks;
  }

  private async moveCalendarFocus(fromIsoDate: string, offsetDays: number): Promise<void> {
    const targetDate = this.addDays(this.parseIsoToDate(fromIsoDate), offsetDays);
    const targetIsoDate = this.toIsoDate(targetDate);

    if (!this.isWithinSelectableRange(targetIsoDate)) {
      return;
    }

    if (!this.isSameCalendarMonth(targetIsoDate, this.currentMonth)) {
      this.currentMonth = this.toMonthStart(targetDate);
      this.loadedMonthKey = '';
      this.generateCalendar();
      await this.refreshCurrentMonthAvailability();
    }

    const targetDay = this.calendarWeeks
      .flat()
      .find((day) => day.isoDate === targetIsoDate);

    if (targetDay && !targetDay.isDisabled) {
      await this.selectCalendarDay(targetDay);
    }

    setTimeout(() => {
      const element = document.querySelector<HTMLButtonElement>(`button[data-calendar-date="${targetIsoDate}"]`);
      element?.focus();
    });
  }

  private isSameCalendarMonth(isoDate: string, compareDate: Date): boolean {
    const parsed = this.parseIsoToDate(isoDate);
    return parsed.getFullYear() === compareDate.getFullYear() && parsed.getMonth() === compareDate.getMonth();
  }

  private isWithinSelectableRange(isoDate: string): boolean {
    return isoDate >= this.minSelectableDate && isoDate <= this.maxSelectableDate;
  }

  private parseIsoDate(value: string): { year: number; month: number; day: number } {
    const [year, month, day] = value.split('-').map((part) => Number.parseInt(part, 10));
    return { year, month, day };
  }

  private parseIsoToDate(value: string): Date {
    const { year, month, day } = this.parseIsoDate(value);
    return new Date(year, month - 1, day);
  }

  private toMonthStart(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private toIsoDate(date: Date): string {
    const normalized = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return normalized.toISOString().slice(0, 10);
  }

  private addDays(date: Date, amount: number): Date {
    const clone = new Date(date);
    clone.setDate(clone.getDate() + amount);
    return clone;
  }

  private executeBookingRequestRecaptcha(): Promise<string> {
    return Promise.race([
      this.recaptchaService.executeBookingRequest(),
      new Promise<string>((_, reject) => {
        setTimeout(() => {
          reject(new Error('reCAPTCHA request timed out.'));
        }, 5000);
      })
    ]);
  }
}
